import React, { useEffect, useState } from "react";
import { OAUTH_REDIRECT_URL } from "@/api/base";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { postNewUser, putTempUserToFullUser } from "@/api/Api";
import { PutTempUserToFullUserRequest } from "@shared_types";
import { callWithAuth } from "@/api/Api";
import { signInWithProvider, SocialSigninResponse } from "@/api/login";
import { useNavigate } from "react-router-dom";
import { Spinner } from "@chakra-ui/react";
import { LoadingPage } from "./LoadingPage";

const RedirectPage: React.FC = () => {
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { search } = useLocation();
  const nav = useNavigate();

  const searchParams = React.useMemo(
    () => new URLSearchParams(search),
    [search]
  );
  const runOnce = React.useRef(false);

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const isNativeIOS = searchParams.get("native") == "true";
    if (!code || !state) {
      setError("No code or state provided");
      setLoading(false);
      return;
    }
    if (runOnce.current) {
      return;
    }

    runOnce.current = true;
    const redirectUrl = OAUTH_REDIRECT_URL + "/redirect";
    const provider = JSON.parse(localStorage.getItem("provider") || "{}");

    // compare the redirect's state param and the stored provider's one
    if (provider.state !== state) {
      setError("State mismatch error");
      setLoading(false);
      return;
    }
    const isApple = provider.name === "apple";
    const skipName = isApple;
    // authenticate
    signInWithProvider(
      provider.name,
      code,
      provider.codeVerifier,
      redirectUrl,
      skipName,
      isNativeIOS
    )
      .then(async (signinResponse: SocialSigninResponse) => {
        if (cameFromPostPaymentSignup()) {
          // IF they signed up with apple, we dont have their name yet.
          const parrotUserId = getParrotUserId();
          if (parrotUserId) {
            try {
              // this is the happy awesome case
              // stay tuned for where we send the user in
              // the future.
              await handleSignUpTempUser(parrotUserId);
            } catch (error) {
              console.error("Failed to handle temp user:", error);
              setError("Failed to handle temp user");
            } finally {
              localStorage.removeItem("isTemp");
              localStorage.removeItem("parrotUserId");
            }
          }
          if (provider.name === "apple") {
            // if user signed up with apple, we need to get their name
            nav(`/name`);
          } else {
            // if they reach this it means they've done onboarding, paid and have now made an account not with apple
            nav("/placement");
          }
          return;
        } else {
          // user wasn't temp before so they either have an account already or they're new and need to do onboarding
          const authData = signinResponse.authData;
          const createdAt = authData.record.created;
          const withinSeconds = 7;
          if (wasJustCreated(createdAt, withinSeconds)) {
            // this would mean they tried to login but didnt already have an account
            // make a popup to let them know they have to do onboarding
            // create the new user
            setLoading(true);
            await callWithAuth(postNewUser);
            return nav("/onboarding");
          } else {
            // they already have an account so we can just redirect them to home
            nav("/home/learn");
          }
        }
      })
      .catch((err) => {
        console.error("Failed to exchange code:", err);
        setError("Failed to exchange code. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [search, nav, searchParams]);

  const cameFromPostPaymentSignup = () => {
    // these two keys mean that they have either started a trial or paid
    // us for premium.
    const isTemp = localStorage.getItem("isTemp") === "true";
    return isTemp;
  };
  const getParrotUserId = () => {
    return localStorage.getItem("parrotUserId");
  };

  const handleSignUpTempUser = async (parrotUserId: string) => {
    const data: PutTempUserToFullUserRequest = {
      parrotUserId: parseInt(parrotUserId),
    };
    await callWithAuth(putTempUserToFullUser, data);
  };

  return (
    <div className="flex flex-col justify-center items-center h-screen">
      {isLoading ? (
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="blue.500"
          size="xl"
        />
      ) : error ? (
        <div>
          <p className="text-center max-w-md">
            Something went wrong. Please try again and contact
            support@fluentwithparrot.com if the issue persists.
          </p>
          <Button
            onClick={() => {
              // correctly send the user back the the retry location
              if (cameFromPostPaymentSignup()) {
                nav("/signup");
              } else {
                nav("/login");
              }
            }}
            className="mt-4"
          >
            Try again
          </Button>
        </div>
      ) : (
        <LoadingPage />
      )}
    </div>
  );
};

export function wasJustCreated(timestamp: string, seconds: number): boolean {
  const currentTime = new Date().getTime(); // Current time in milliseconds
  const inputTime = new Date(timestamp).getTime(); // Input timestamp in milliseconds
  const differenceInSeconds = (currentTime - inputTime) / 1000; // Difference in seconds
  return differenceInSeconds <= seconds;
}

export default RedirectPage;
